import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { GroupEntity } from '@backstage/catalog-model';
import {
  collectDescendants,
  DEFAULT_NAMESPACE,
  groupRefOf,
  hasExplicitWobo,
  resolveEffectiveWobo,
  type OwnWobo,
  type WoboGroupLike,
} from '@internal/backstage-catalog-utils';
import { entityRefToCatalogPath } from '../components/shared/entityRefs';

export type GroupSummary = {
  ref: string;
  title: string;
  path: string;
  /** False when the group is not in the catalog (title falls back to the ref). */
  found: boolean;
};

export interface WoboContext {
  loading: boolean;
  error?: Error;
  own: OwnWobo;
  /** Effective target: own explicit, else inherited. */
  target?: GroupSummary;
  inherited?: { target: GroupSummary; from: GroupSummary };
  /** Team-level descendants that inherit from THIS group. */
  inheritingTeamCount: number;
}

type FetchedGroup = WoboGroupLike & { metadata: { title?: string } };

const FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'spec.type',
  'spec.parent',
  'spec.worksOnBehalfOf',
];

/**
 * Resolves a Group's own and inherited worksOnBehalfOf (PAT-1066) from one
 * projected Group fetch per mount. `overrides` previews unsaved edit-form
 * values. On a fetch failure only `own` (from the entity alone) is usable.
 */
export function useWoboContext(
  entity: GroupEntity,
  overrides?: { parent?: string; worksOnBehalfOf?: string },
  enabled: boolean = true,
): WoboContext {
  const catalogApi = useApi(catalogApiRef);
  const [groups, setGroups] = useState<FetchedGroup[]>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Group' }, fields: FIELDS })
      .then(({ items }) => {
        if (!cancelled) setGroups(items as unknown as FetchedGroup[]);
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, enabled]);

  const parent = overrides ? overrides.parent : undefined;
  const wobo = overrides ? overrides.worksOnBehalfOf : undefined;
  const hasOverrides = Boolean(overrides);

  return useMemo(() => {
    const self: WoboGroupLike = {
      metadata: entity.metadata,
      spec: {
        parent: hasOverrides ? parent : entity.spec?.parent,
        worksOnBehalfOf: hasOverrides
          ? wobo
          : (entity.spec as { worksOnBehalfOf?: unknown } | undefined)
              ?.worksOnBehalfOf,
      },
    };
    const selfRef = groupRefOf(self);

    if (!groups) {
      const own = resolveEffectiveWobo(self, () => undefined).own;
      return {
        loading: enabled && !error,
        error,
        own,
        inheritingTeamCount: 0,
      };
    }

    const byRef = new Map<string, FetchedGroup>(
      groups.map(g => [groupRefOf(g), g]),
    );
    const resolver = (ref: string) => (ref === selfRef ? self : byRef.get(ref));
    const result = resolveEffectiveWobo(self, resolver);

    const summarize = (ref: string): GroupSummary => {
      const g = byRef.get(ref);
      return {
        ref,
        found: Boolean(g),
        title: g?.metadata.title ?? g?.metadata.name ?? ref,
        path: entityRefToCatalogPath(ref, {
          defaultKind: 'group',
          defaultNamespace: DEFAULT_NAMESPACE,
        }),
      };
    };

    let targetRef: string | undefined;
    if (result.own.kind === 'explicit') {
      targetRef = result.own.targetRef;
    } else if (result.own.kind === 'none') {
      targetRef = result.inherited?.targetRef;
    }

    const withSelf = [self, ...groups.filter(g => groupRefOf(g) !== selfRef)];
    const inheritingTeamCount = collectDescendants(selfRef, withSelf, {
      stopAt: hasExplicitWobo,
    }).filter(g => (g.spec?.type ?? 'team') === 'team').length;

    return {
      loading: false,
      own: result.own,
      target: targetRef ? summarize(targetRef) : undefined,
      inherited: result.inherited
        ? {
            target: summarize(result.inherited.targetRef),
            from: summarize(result.inherited.fromRef),
          }
        : undefined,
      inheritingTeamCount,
    };
  }, [entity, groups, error, enabled, hasOverrides, parent, wobo]);
}
